<template>
  <ContentBlock>
    <div slot="instructions">
      <h5>{{ $t('Supported Languages') }}</h5>
    </div>
    <div>
      <div class="f-thead grid bb pb3 mb4 b--geyser">
        <span class="mr-auto">{{ $t('Languages') }}</span>
        <span>{{ $t('Interface') }}</span>
        <span>{{ $t('Full Audio') }}</span>
        <span>{{ $t('Subtitles') }}</span>
        <span>&nbsp;</span>
      </div>
      <ul>
        <li
          class="grid items-center mb4"
          v-for="(language, i) in selectedLanguages"
          :key="language.language"
        >
          <UIMultiselect
            class="mr6"
            :style="{zIndex: 100-i}"
            :value="{ key: language.key, title: language.title }"
            @input="({ key }) => language.key = key"
            :options="languages || []"
            :internalSearch="true"
            :loading="loading"
            open-direction="bottom"
            label="title"
            track-by="key"
            placeholder="Type to search"
          ></UIMultiselect>
          <UISwitch required v-model="language.interface" />
          <UISwitch required v-model="language.fullAudio" />
          <UISwitch required v-model="language.subtitles" />
          <UIButton
            icon="trash"
            tiny
            v-if="selectedLanguages && selectedLanguages.length > 1"
            @click="doRemoveLanguage(language)"
          ></UIButton>
        </li>
      </ul>
      <UIButton class="mt4" tiny icon="plus" @click="doAddLanguage">{{ $t('Add a language') }}</UIButton>
    </div>
  </ContentBlock>
</template>

<script>
import ContentBlock from '@/core/shared/components/ContentBlock';
// import UIInput from '@/core/shared/components/ui/UIInput';
import UISwitch from '@/core/shared/components/ui/UISwitch';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIMultiselect from '@/core/shared/components/ui/UIMultiselect';
import useLanguages from '@/core/compositions/useLanguages';
import Vue from 'vue';
import { keyBy, cloneDeep, isEqual } from 'lodash-es';
import { computed, watch, reactive } from '@vue/composition-api';
import { useLazyComputed } from '@/core/shared/composition-helpers';

const DEFAULT_LANGUAGE = 'en';
const defaultLanguage = {
  language: DEFAULT_LANGUAGE,
  fullAudio: true,
  interface: true,
  subtitles: true,
};

export default {
  name: 'ContentLanguageForm',
  components: {
    ContentBlock,
    UISwitch,
    UIButton,
    UIMultiselect,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
  },
  setup (props, { emit }) {
    const { languages, loading } = useLanguages();
    const contentOwnershipLanguages = useLazyComputed(() => {
      return props.contentOwnership.contentOwnershipLanguages && props.contentOwnership.contentOwnershipLanguages.length > 0
        ? cloneDeep(props.contentOwnership.contentOwnershipLanguages)
        : [defaultLanguage];
    }, (newValue, oldValue) => !isEqual(newValue, oldValue));
    const languagesById = computed(() => languages.value && keyBy(languages.value, 'key'));
    const selectedLanguages = computed(() => {
      if (!languages.value) return;
      const reactiveObj = reactive({
        arr: contentOwnershipLanguages.value.map(({
          language,
          displayLanguage,
          ...rest
        }) => ({ key: language, title: languagesById.value[language]?.title, ...rest })),
      });
      return reactiveObj.arr;
    });
    function doRemoveLanguage ({ key }) {
      const index = contentOwnershipLanguages.value.findIndex(({ language }) => language === key);
      Vue.delete(contentOwnershipLanguages.value, index);
    }
    function doAddLanguage () {
      const language = { ...defaultLanguage, language: null };
      contentOwnershipLanguages.value.push(language);
    }
    watch(() => selectedLanguages.value, (languages) => {
      if (!languages) return;
      const formattedValue = languages.map((val) => ({
        language: val.key,
        interface: val.interface,
        fullAudio: val.fullAudio,
        subtitles: val.subtitles,
      }));
      emit('updateLanguage', formattedValue);
    }, { deep: true });
    return {
      languages,
      selectedLanguages,
      loading,
      doRemoveLanguage,
      doAddLanguage,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.grid {
  display: grid;
  grid-template-columns: 1fr repeat(3, minmax(auto, 100px)) minmax(40px, auto);
}
</style>
